import { useQueryClient } from '@tanstack/react-query';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useState } from 'react';
import { CODE_REQUIRE_TWO_FACTOR } from '@/constants/error-codes';
import { PATH_URL } from '@/constants/path-constants';
import { fetchOnboardingStepsQueryCreator } from '@/query/fetchOnboardingStepsQueryCreator';
import { useFinishOnboardingStepMutation } from '@/query/mutations/useFinishOnboardingStepMutation';
import { useAuthNavigation } from '@/shared/hooks/useAuthNavigation';
import type {
  FinishOnboardingStepType,
  NEW_ONBOARDING_FLOW,
  NEW_ONBOARDING_STEP,
} from '@/shared/types/welcome';
import { getOnboardingStepFromPath } from '../utils/utils';

export function useFinishOnboardingStep({
  onSuccess,
  onError,
}: {
  onSuccess?: (res: FinishOnboardingStepType) => void;
  onError?: (error: {
    status?: string;
    message?: string;
    code?: number;
  }) => void;
}) {
  const queryClient = useQueryClient();
  const searchParams = useSearchParams();
  const pathName = usePathname();
  const router = useRouter();
  const { goTo2FACode } = useAuthNavigation();
  const [loading, setLoading] = useState(false);

  const onboardingStep = getOnboardingStepFromPath(pathName);
  const onboardingFlow = searchParams.get('new_onboarding');

  const { mutateAsync, isPending: isFinishingOneStepOfOnboarding } =
    useFinishOnboardingStepMutation({
      onSuccess: async (res) => {
        setLoading(true);
        if (onSuccess) {
          onSuccess(res);
        }

        if (!onboardingFlow) {
          router.push(`/${PATH_URL.HOME}`);
          return;
        }

        await queryClient.fetchQuery(
          fetchOnboardingStepsQueryCreator({
            flow_name: onboardingFlow as NEW_ONBOARDING_FLOW,
            ...(onboardingStep && {
              last_completed_step: onboardingStep as NEW_ONBOARDING_STEP,
            }),
            additionalOptions: {
              staleTime: 0,
            },
          }),
        );

        setLoading(false);
      },
      onError: (error) => {
        setLoading(true);
        const customError = error as Error & { status?: string; code?: number };

        if (onError)
          onError({
            status: customError.status,
            message: customError.message,
            code: customError.code,
          });

        if (customError.code === CODE_REQUIRE_TWO_FACTOR) {
          goTo2FACode({ redirectUrl: PATH_URL.WELCOME });
        } else {
          router.push(`/${PATH_URL.HOME}`);
        }
      },
    });

  const finishOnboardingStepAsync = useCallback(
    ({ skipped, answer }: { skipped?: boolean; answer?: string }) => {
      if (!onboardingStep) {
        throw new Error('Onboarding step is missing');
      }

      return mutateAsync({
        name: onboardingStep,
        skipped,
        answer,
      });
    },
    [onboardingStep, mutateAsync],
  );

  const isFinishingOnboarding = isFinishingOneStepOfOnboarding || loading;

  return {
    finishOnboardingStepAsync,
    isFinishingOnboarding,
  };
}
